/**
 * Inspection photos → Supabase Storage (bucket `inspection-photos`).
 *
 * Photos are shrunk on the phone first (PhotoSlot), kept as data URLs so the inspection works
 * with no signal, and swapped for an online link as soon as one uploads. The report link and the
 * Zoho PDF both read the online link, so anything still on the phone uploads before the report goes out.
 *
 * Each file gets a random name (UUID) — the link works with no login but can't be guessed,
 * and the bucket can't be listed. Only signed-in, active staff can upload (storage policy).
 * Without Supabase (local login / tests) photos simply stay on the phone.
 */
import { getSupabase } from './auth';

export const PHOTO_BUCKET = 'inspection-photos';

const isDataUrl = (src) => typeof src === 'string' && src.startsWith('data:');
export const isUploaded = (src) => typeof src === 'string' && /^https?:\/\//.test(src);

const dataUrlToBlob = (dataUrl) => {
  const [head, b64] = dataUrl.split(',');
  const type = (head.match(/data:([^;]+)/) || [])[1] || 'image/jpeg';
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return new Blob([bytes], { type });
};

const randomId = () =>
  (typeof crypto !== 'undefined' && crypto.randomUUID)
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}${Math.random().toString(36).slice(2)}`;

/* One photo up; returns its public link. Throws if it can't upload (offline, signed out …). */
export const uploadPhoto = async (dataUrl) => {
  const supabase = getSupabase();
  if (!supabase) throw new Error('Photo storage not connected');
  const blob = dataUrlToBlob(dataUrl);
  const month = new Date().toISOString().slice(0, 7); // 2026-10/…
  const path = `${month}/${randomId()}.jpg`;
  const { error } = await supabase.storage
    .from(PHOTO_BUCKET)
    .upload(path, blob, { contentType: blob.type, cacheControl: '31536000', upsert: false });
  if (error) throw error;
  return supabase.storage.from(PHOTO_BUCKET).getPublicUrl(path).data.publicUrl;
};

// the same photo is only ever uploaded once per session (a draft can save again while it's still on the phone)
const uploaded = new Map();

/* Best effort: the online link if it uploads, else the photo stays on the phone for later. */
export const tryUpload = async (src) => {
  if (!isDataUrl(src) || !getSupabase()) return src;
  if (uploaded.has(src)) {
    try {
      return await uploaded.get(src);
    } catch {
      return src; // that upload failed — the caller's own attempt reports it
    }
  }
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return src;
  try {
    const link = uploadPhoto(src);
    uploaded.set(src, link);
    const url = await link;
    uploaded.set(src, url);
    return url;
  } catch (err) {
    uploaded.delete(src);
    console.warn('Photo kept on the phone — will upload later:', err.message || err);
    return src;
  }
};

/* Before a report goes out: upload whatever is still on the phone, one at a time.
   Takes a list of srcs; returns { list, pending } — pending > 0 means some are still local. */
export const uploadPending = async (srcs = []) => {
  const list = [];
  for (const src of srcs) list.push(await tryUpload(src));
  return { list, pending: list.filter(isDataUrl).length };
};
